/**
 * The set-role menu behind a matrix row's badge — the mock's `typeMenu`.
 *
 * ── It calls `changeSetType` and nothing else ───────────────────────────────────────────────
 * Retyping a set is NOT part of the rule the sheet is staging. The set table two boxes away already
 * authors roles through this operation, it is its own draft write and its own undo entry, and the
 * staging reducer deliberately left `roleChange` out for exactly that reason (see its header). A
 * second role writer here would be the near-duplicate this repo's rules exist to prevent, and it
 * would also mean a role change vanishing on „Anuluj" in one surface and standing in the other.
 *
 * The matrix re-derives from the plan's own rows — `stagedProjection` lays only the staged RULE over
 * them — so the row's badge and every cell on it follow the write on the next render with nothing to
 * keep in step.
 */

import type { CSSProperties } from 'react';

import {
  AUTHORABLE_SET_TYPES,
  changeSetType,
  setTypeRefusal,
  type ExerciseDraft,
} from '~/entities/plan-draft';
import menuStyles from '~/shared/ui/MenuSurface.module.css';

import type { Translate } from '../model/matrixCellState';

import { RefusableRadioMenu } from './RefusableRadioMenu';
import styles from './SheetMatrix.module.css';

const KEY = 'web:planBuilder.progression.sheet.type';

/** The wire's type name → the word this app words it with, and the badge's own colour key. */
const KIND: Readonly<Record<string, string>> = {
  Warmup: 'warmup',
  Working: 'working',
  Top: 'top',
  Backoff: 'backoff',
  AMRAP: 'amrap',
};

interface TypeMenuProps {
  readonly exercise: ExerciseDraft;
  readonly setIndex: number;
  /** The badge's letter and the row's accessible name, already worded by `matrixCellState`. */
  readonly badge: { readonly letter: string; readonly kind: string; readonly label: string };
  readonly rowLabel: string;
  /** This row's header holds the grid's one tab stop. */
  readonly hasFocus: boolean;
  /** `row-column`, the address `shared/lib/useGridFocus` moves focus by. Column 0 is the header. */
  readonly gridPosition: string;
  /** Which step of the grid's fold-in stagger this header is on. */
  readonly foldStep: number;
  readonly onFocus: () => void;
  readonly t: Translate;
}

/**
 * ── The trigger IS the row header ───────────────────────────────────────────────────────────
 * `role="rowheader"`, not a bare button inside a `role="row"`: a row of a grid whose first cell has
 * no header role is a row a screen reader cannot name, and a separate trigger inside it would be one
 * extra tab stop per row on a grid whose whole point is to have one. So the header carries the
 * roving tab index and Enter, Space and typeahead open the menu from it — which is also the keyboard
 * path to every role change the mock puts behind this badge.
 */
export function TypeMenu({
  exercise,
  setIndex,
  badge,
  rowLabel,
  hasFocus,
  gridPosition,
  foldStep,
  onFocus,
  t,
}: TypeMenuProps) {
  const set = exercise.sets[setIndex - 1];
  const currentType = set?.setType ?? null;

  return (
    <RefusableRadioMenu
      triggerProps={{
        /* A row HEADER, not a button, and Base UI has to be told: `nativeButton` defaults to true
          * and `useButton` logs an error on every open of a menu whose trigger is not a real
          * `<button>`. With it false the keyboard path is Base UI's own — Enter and Space open the
          * menu from an element that is not a button — and `role` is restated here rather than on
          * the rendered element, because a non-native trigger merges `role="button"` under the
          * caller's own props. */
        render: <span />,
        nativeButton: false,
        role: 'rowheader',
        className: styles.rowLabel,
        'aria-label': rowLabel,
        'data-cell': gridPosition,
        style: { '--fold-step': String(foldStep) } as CSSProperties,
        tabIndex: hasFocus ? 0 : -1,
        onFocus,
        children: (
          <>
            <span className={styles.rowBadge} data-kind={badge.kind} aria-hidden="true">
              {badge.letter}
            </span>
            <span className={styles.rowIndex} aria-hidden="true">
              {t(`${KEY}.rowIndex`, { index: setIndex })}
            </span>
          </>
        ),
      }}
      label={t(`${KEY}.menuLabel`, { index: setIndex })}
      value={currentType ?? ''}
      options={AUTHORABLE_SET_TYPES.map((candidate) => {
        const kind = KIND[candidate] ?? 'working';
        const refusal = setTypeRefusal(candidate, currentType, exercise.measurementFields);
        return {
          value: candidate,
          isRefused: refusal !== null,
          content: (
            <span className={styles.typeEntry}>
              <span className={styles.typeBadge} data-kind={kind} aria-hidden="true">
                {candidate.slice(0, 1)}
              </span>
              <span className={menuStyles.itemLabel}>{t(`${KEY}.${kind}.name`)}</span>
              <span className={styles.typeNote}>
                {refusal === null ? t(`${KEY}.${kind}.desc`) : t(`${KEY}.refusal.${refusal}`)}
              </span>
            </span>
          ),
        };
      })}
      onPick={(candidate) => {
        changeSetType({ exerciseSlotToken: exercise.slotToken, setIndex }, candidate);
      }}
    />
  );
}
